import React, { ReactNode } from 'react';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SxProps, styled } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import './TwSelects.scss';
import Stack from '@mui/material/Stack';
import { MenuProps } from '@mui/material/Menu';
import TwTooltip from '../TwTooltip/TwTooltip';
export interface TwSelectOption {
  label: string;
  value: string;
  icon?: ReactNode;
  tooltipTitle?: string;
}

export interface TwSelectProps {
  /**
   * Label text for the Select.
   */
  label?: string;
  /**
   * Id for the Select.
   */
  id?: string;
  /**
   * Select editable status.
   */
  disabled?: boolean;
  /**
   * Text for the placeholder, if any.
   */
  placeholder?: string;
  /**
   * Classes to style the component.
   */
  className?: string;
  /**
   * Callback fired when the value is changed.
   */
  onInputChange?: (e: SelectChangeEvent<string>) => void;
  /**
   * Select name.
   */
  name: string;
  /**
   * Sx props to style the component
   */
  sx?: SxProps;
  /**
   * Select option list
   */
  optionList: TwSelectOption[];
  /**
   * Select value
   */
  value: string | undefined;
  /**
   * Select has error flag
   */
  error?: boolean;
  /**
   * Helper text for the errors
   */
  helperText?: string | undefined;
  /**
   * Label icon
   */
  labelIcon?: ReactNode;
  /**
   * Hide arrow icon
   */
  hideArrowIcon?: boolean;
  /**
   * Sx props to style the Select component
   */
  selectSx?: SxProps;
  fullWidth?: boolean;
  /**
   *  Show a checkmark icon on the option selected
   */
  showCheckmark?: boolean;
  /**
   * Props for the menu container
   */
  menuProps?: Partial<MenuProps>;
  /**
   * show a custom render value
   */
  renderValue?: (value: string) => ReactNode;
  /**
   * Disable portal for the menu
   */
  disablePortal?: boolean;
}

const StyledSelectFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'value'
})<{ value: string | undefined }>((value) => ({
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 'normal',
  fontSize: '14px',
  lineHeight: '21px',
  backgroundColor: 'none',
  width: '100%',
  height: '40px',
  padding: '0',

  '& .MuiOutlinedInput-root fieldset.MuiOutlinedInput-notchedOutline': {
    borderColor: '#E2E2EA'
  },

  '& input': {
    fontFamily: 'Poppins',
    fontSize: '14px',
    borderColor: '#E2E2EA'
  },

  '& .Mui-focused': {
    '& fieldset.MuiOutlinedInput-notchedOutline': {
      borderColor: '#3959df'
    }
  },

  '& .MuiSelect-select': {
    color: !!value ? 'black' : '#B5B5BE',
    background: 'white',
    padding: '9.5px 14px'
  },

  '& .MuiModal-root.MuiPopover-root.MuiMenu-root': {
    top: '0'
  }
}));

const TwSelect: React.FC<TwSelectProps> = ({
  id,
  label,
  className,
  name,
  value,
  disabled,
  optionList,
  placeholder,
  labelIcon,
  hideArrowIcon,
  menuProps,
  sx,
  selectSx,
  onInputChange,
  fullWidth,
  showCheckmark = false,
  renderValue,
  disablePortal = false
}) => {
  const customArrow = (props: { [key: string]: string }): JSX.Element => {
    return (
      <ExpandMoreIcon
        className={`${props.className} tw-selects__icon`}
        sx={{ display: hideArrowIcon ? 'none' : undefined }}
      />
    );
  };

  return (
    <StyledSelectFormControl
      fullWidth={fullWidth}
      value={value}
      className={`tid-select ${className}`}
      sx={sx}
    >
      {label && (
        <label className="tw-selects__label tid-select-label" htmlFor={id}>
          <span style={{ padding: `${!!labelIcon ? '2px 0' : '4px 0'}` }}>{label}</span>
          {!!labelIcon && labelIcon}
        </label>
      )}
      <Select
        className="tw-selects__default"
        labelId={`${name}-${label}`}
        id={id}
        name={name}
        disabled={disabled}
        value={!!value ? value : ''}
        onChange={onInputChange}
        IconComponent={customArrow}
        classes={{
          iconOpen: 'tw-selects__iconOpen'
        }}
        MenuProps={{
          ...menuProps,
          disablePortal: disablePortal
        }}
        displayEmpty
        sx={{
          ...selectSx,
          '& .MuiSelect-select': {
            color: !!value ? 'black' : '#B5B5BE'
          }
        }}
        renderValue={renderValue}
      >
        {placeholder && (
          <MenuItem value={''} className="tw-selects__option tw-selects__placeholder}">
            {placeholder}
          </MenuItem>
        )}

        {optionList.map((option, index): JSX.Element => {
          return (
            <MenuItem className="tw-selects__option" value={option.value} key={index}>
              <TwTooltip title={option.tooltipTitle} placement="top">
                <Stack direction="row" alignItems="center" className="tw-selects__text-container">
                  {option.icon && option.icon}
                  {option.label}
                </Stack>
              </TwTooltip>

              {showCheckmark && value === option.value && (
                <CheckRoundedIcon sx={{ color: '#3A4DE9', fontSize: '18px' }} />
              )}
            </MenuItem>
          );
        })}
      </Select>
    </StyledSelectFormControl>
  );
};

TwSelect.defaultProps = {
  disabled: false,
  error: false
};

export default TwSelect;
